import type { ReactNode } from "react";

import { useI18n, type MessageKey } from "~/i18n";
import { cn } from "~/lib/utils";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";

const FILTER_TRIGGER =
  "h-8 min-h-8 w-full min-w-0 gap-1 border-0 bg-transparent px-1.5 shadow-none hover:bg-transparent sm:w-auto sm:min-w-[6.5rem] sm:max-w-[9.5rem]";

const MINIMAL_TRIGGER =
  "h-7 min-h-7 w-auto max-w-[7.25rem] gap-1 border-0 bg-transparent px-1.5 shadow-none hover:bg-muted/45 data-[popup-open]:bg-muted/55";

export function ActivityFilterBox({
  icon,
  labelKey,
  value,
  active,
  onValueChange,
  children,
  className,
  variant = "default",
  valueLabel,
}: {
  readonly icon: ReactNode;
  readonly labelKey: MessageKey;
  readonly value: string;
  readonly active: boolean;
  readonly onValueChange: (value: string) => void;
  readonly children: ReactNode;
  readonly className?: string;
  readonly variant?: "default" | "minimal";
  /** Shown in the minimal trigger when a non-default filter is active. */
  readonly valueLabel?: string | null;
}) {
  const { t } = useI18n();
  const label = t(labelKey);

  if (variant === "minimal") {
    const triggerLabel =
      valueLabel && valueLabel.length > 0
        ? `${label}: ${valueLabel}`
        : `${label}: ${t("activities.filter.tapToChoose")}`;

    return (
      <Select
        value={value}
        onValueChange={(next) => {
          if (next) onValueChange(next);
        }}
      >
        <SelectTrigger
          size="sm"
          className={cn(
            MINIMAL_TRIGGER,
            active && "text-primary hover:bg-primary/8 data-[popup-open]:bg-primary/10",
            className,
          )}
          aria-label={triggerLabel}
          title={triggerLabel}
        >
          <span
            className={cn(
              "flex shrink-0 items-center justify-center text-muted-foreground [&_svg]:size-3.5",
              active && "text-primary",
            )}
            aria-hidden
          >
            {icon}
          </span>
          <span
            className={cn(
              "min-w-0 truncate text-[11px] font-medium",
              valueLabel ? "text-foreground" : "text-muted-foreground",
            )}
          >
            {valueLabel || label}
          </span>
        </SelectTrigger>
        <SelectPopup align="end" matchTriggerWidth className="min-w-[10rem]">
          {children}
        </SelectPopup>
      </Select>
    );
  }

  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-1 rounded-lg border border-border/65 bg-muted/15 py-0.5 ps-1.5 pe-0.5 shadow-xs/5 transition-colors",
        active && "border-primary/35 bg-primary/[0.06]",
        className,
      )}
    >
      <div className="flex min-w-0 shrink-0 items-center gap-1.5">
        <span
          className={cn(
            "flex size-7 shrink-0 items-center justify-center rounded-md bg-background/90 text-muted-foreground ring-1 ring-inset ring-border/55",
            active && "bg-primary/12 text-primary ring-primary/25",
          )}
          aria-hidden
        >
          {icon}
        </span>
        <span className="truncate text-xs font-medium text-muted-foreground">{label}</span>
      </div>
      <Select
        value={value}
        onValueChange={(next) => {
          if (next) onValueChange(next);
        }}
      >
        <SelectTrigger size="sm" className={FILTER_TRIGGER} aria-label={label}>
          <SelectValue className="truncate text-xs font-medium text-foreground" />
        </SelectTrigger>
        <SelectPopup align="end" matchTriggerWidth>
          {children}
        </SelectPopup>
      </Select>
    </div>
  );
}

export function ActivityFilterAllItem({ labelKey }: { readonly labelKey: MessageKey }) {
  const { t } = useI18n();
  return <SelectItem value="all">{t(labelKey)}</SelectItem>;
}
